import { createContext, useCallback, useContext, useMemo, useState } from "react";

import {
    LendingVaultExpanded,
    UserVaultPositionExpanded,
    useUserVaultPositionByVault,
    useUserVaultPositionExternalRewards,
    useUserVaultDepositSummaryByVault,
    useLendVaultAccessGate,
    DEFAULT_LP_SLIPPAGE,
    AggregatedUserExternalRewardsPnlResponse,
    convertExternalRewardsToAggregatedRewards,
    VaultDepositSummary
} from "@bridgesplit/abf-react";
import { DispatchType } from "@bridgesplit/react";

interface VaultContextProps {
    vaultAddress: string | undefined;
    vaultExpanded: LendingVaultExpanded | undefined;
}

interface VaultContextState {
    vaultAddress: string | undefined;
    vaultExpanded: LendingVaultExpanded | undefined;
    // Access-aware: an allowlisted wallet sees a coming-soon vault as a normal live vault.
    isComingSoon: boolean;
    isVaultAllowed: boolean;
    isVaultAccessLoading: boolean;
    userPosition: UserVaultPositionExpanded | undefined;
    userPositionLoading: boolean;
    userPositionFetching: boolean;
    userExternalRewardsPosition: AggregatedUserExternalRewardsPnlResponse | null | undefined;
    userExternalRewardsPositionLoading: boolean;
    isActionOpen: boolean;
    setIsActionOpen: DispatchType<boolean>;

    vaultDepositSummary: VaultDepositSummary | undefined;
    vaultDepositSummaryLoading: boolean;
    vaultDepositSummaryFetching: boolean;
    slippageSetting: number;
    setSlippageSetting: DispatchType<number>;
    resetSlippageSetting: () => void;
}

const VaultContext = createContext<VaultContextState | null>(null);

export function useVaultContext(): VaultContextState {
    const context = useContext(VaultContext);
    if (context === null) {
        throw new Error("useVaultContext must be used within a VaultProvider");
    }
    return context;
}

export function VaultProvider({
    children,
    ...props
}: {
    children: React.ReactNode;
} & VaultContextProps) {
    const [isActionOpen, setIsActionOpen] = useState(false);

    const [slippageSetting, setSlippageSetting] = useState<number>(DEFAULT_LP_SLIPPAGE);

    const resetSlippageSetting = useCallback(() => {
        setSlippageSetting(DEFAULT_LP_SLIPPAGE);
    }, [setSlippageSetting]);

    const { isComingSoon, isAllowed, isAccessLoading } = useLendVaultAccessGate(props.vaultExpanded);

    const {
        data: userPositions,
        isLoading: userPositionLoading,
        isFetching: userPositionFetching
    } = useUserVaultPositionByVault(props.vaultExpanded);

    const { data: userExternalRewardsPosition, isLoading: userExternalRewardsPositionLoading } =
        useUserVaultPositionExternalRewards(props.vaultExpanded);

    const userPosition: UserVaultPositionExpanded | undefined = useMemo(() => {
        if (!props.vaultExpanded || userPositionLoading || userPositionFetching) return undefined;
        return userPositions?.find((p) => p.vaultExpanded.vault.address === props.vaultExpanded?.vault.address);
    }, [userPositions, props.vaultExpanded, userPositionLoading, userPositionFetching]);

    //Aggregated external rewards pnl for the user's stake in the vault.
    const vaultExternalRewardsAggregated: AggregatedUserExternalRewardsPnlResponse | null | undefined = useMemo(() => {
        return convertExternalRewardsToAggregatedRewards(props.vaultExpanded, userExternalRewardsPosition);
    }, [props.vaultExpanded, userExternalRewardsPosition]);

    const {
        data: vaultDepositSummary,
        isLoading: vaultDepositSummaryLoading,
        isFetching: vaultDepositSummaryFetching
    } = useUserVaultDepositSummaryByVault(props.vaultExpanded?.vault.address);

    return (
        <VaultContext.Provider
            value={{
                ...props,
                isComingSoon,
                isVaultAllowed: isAllowed,
                isVaultAccessLoading: isAccessLoading,
                isActionOpen,
                setIsActionOpen,
                userPosition,
                userPositionLoading,
                userPositionFetching,
                userExternalRewardsPosition: vaultExternalRewardsAggregated,
                userExternalRewardsPositionLoading: userExternalRewardsPositionLoading,
                slippageSetting,
                setSlippageSetting,
                resetSlippageSetting,
                vaultDepositSummary,
                vaultDepositSummaryLoading,
                vaultDepositSummaryFetching
            }}
        >
            {children}
        </VaultContext.Provider>
    );
}
